import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { ShieldCheck, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogin() {
  const { login, verify } = useAdminAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [challengeId, setChallengeId] = useState(null);
  const [loading, setLoading] = useState(false);

  const submitStep1 = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      setChallengeId(data.challenge_id);
      setStep(2);
      toast.success(data.email_sent ? "Code envoyé par e-mail" : "Code généré — vérifiez la console (mode dev)");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Identifiants invalides");
    } finally {
      setLoading(false);
    }
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verify(challengeId, otp.trim());
      toast.success("Connexion réussie");
      nav("/admin", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code incorrect");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b1220] text-slate-100 flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs uppercase tracking-widest">
            <ShieldCheck className="w-3.5 h-3.5" /> EmpCo · Console Admin
          </div>
          <h1 className="mt-4 text-3xl font-semibold">Accès sécurisé</h1>
          <p className="mt-2 text-slate-400 text-sm">Espace strictement réservé aux administrateurs. Authentification à deux facteurs par e-mail.</p>
        </div>

        <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-8 shadow-2xl">
          {step === 1 && (
            <form onSubmit={submitStep1} className="space-y-4" data-testid="admin-login-step1">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">E-mail administrateur</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-login-email"
                  autoComplete="email"
                  className="w-full bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500/50"
                  placeholder="nicolas@philizor.net"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Mot de passe</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-login-password"
                  autoComplete="current-password"
                  className="w-full bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500/50"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-submit"
                className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-medium py-2.5 rounded-md text-sm inline-flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Continuer
              </button>
              <div className="text-center">
                <Link to="/admin/forgot" className="text-xs text-slate-500 hover:text-emerald-400" data-testid="admin-login-forgot">Mot de passe oublié ?</Link>
              </div>
            </form>
          )}
          {step === 2 && (
            <form onSubmit={submitStep2} className="space-y-4" data-testid="admin-login-step2">
              <div className="flex items-center gap-2 text-xs text-emerald-400 mb-1">
                <KeyRound className="w-3.5 h-3.5" /> Code envoyé à <span className="font-mono">{email}</span>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Code à 6 chiffres</label>
                <input
                  type="text"
                  required
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  data-testid="admin-login-otp"
                  className="w-full bg-[#0b1220] border border-slate-700 rounded-md px-3 py-3 text-center font-mono text-2xl tracking-[0.5em] focus:outline-none focus:border-emerald-500/50"
                  placeholder="••••••"
                />
                <p className="mt-2 text-[11px] text-slate-500">Le code expire dans 10 minutes.</p>
              </div>
              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                data-testid="admin-login-verify"
                className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-medium py-2.5 rounded-md text-sm inline-flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Vérifier et se connecter
              </button>
              <button
                type="button"
                onClick={() => { setStep(1); setOtp(""); }}
                className="w-full text-xs text-slate-500 hover:text-slate-300"
                data-testid="admin-login-back"
              >
                Retour
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-600">
          Espace administrateur EmpCo · Toute tentative d'accès est journalisée.
        </p>
      </div>
    </div>
  );
}
